import { z } from 'zod';
import { contentStatusSchema, lessonCastSchema, lessonPropsSchema, lessonSummarySchema, personaLabelSchema, SCENE_BUCKETS_MAX, SCENE_ITEMS_MAX, sceneArms, sceneBucketSchema, sceneItemSchema, sceneSchema, servedBody, servedHint, tintFields, tagSlugSchema, } from './content.js';
import { contentReviewSchema, storySentencesSchema } from './generation.js';
import { qaScorecardSchema } from './qa-score.js';
import { sceneWarningSchema } from './scene-fix.js';
import { lessonTermSchema } from './terms.js';
/**
 * What a **reviewer** sees — spec 013 §10.3, and the mirror image of `content.ts`.
 *
 * A third contract rather than a widening of the first: `api-conventions.md` §8 strips
 * every answer key from a learner's read, so adding them back would leak the key to every
 * learner. Why that trade lands here is `docs/proof/GEN-06a/shapes.txt`.
 *
 * Role-gated at the route. Not in the package barrel — import
 * `@platform/schema/review`.
 */
/**
 * The four key columns `012` §7 puts on `SceneItem`, all optional because each kind
 * uses exactly one. The mapping is `answerKeyByKind` in `content.ts`; this does not
 * restate it, because a shape that allowed only the right one per kind would be four
 * item types for a difference no renderer cares about.
 */
export const reviewSceneItemSchema = sceneItemSchema
    .extend({
    isCorrect: z.boolean().optional().describe('CHOICE — is this the answer.'),
    correctPosition: z
        .number()
        .int()
        .optional()
        .describe('RANK — where this belongs.'),
    correctBucketId: z
        .string()
        .optional()
        .describe('BUCKET — which bucket this belongs in.'),
    correctAmount: z
        .number()
        .optional()
        .describe('ALLOCATE — the author’s own split for this category.'),
    amountTolerance: z
        .number()
        .optional()
        .describe('ALLOCATE — how far either side of it still counts.'),
})
    .describe('An item with the key that grades it. Never served to a learner.');
/**
 * Whether the learner is served the hint and the subheadline. On every arm, because the
 * text rides the served shape: a reviewer needs it while it is off, so they can turn it
 * back on.
 */
const drafted = {
    hintEnabled: z.boolean().describe('Whether the hint lightbulb is shown.'),
    bodyEnabled: z.boolean().describe('Whether the subheadline is shown.'),
    bodyPredatesRules: z
        .boolean()
        .describe('Written before the subheadline rules and untouched since, so breaking them is a warning rather than a blocker.'),
    warnings: z
        .array(sceneWarningSchema)
        .default([])
        .describe('Every rule the scene breaks, by field, and which AI may fix.'),
};
/** Carried by every scene that has a question behind it. */
const answered = {
    questionId: z
        .string()
        .optional()
        .describe('The row a hand-corrected explanation is written back to.'),
    explanation: z
        .string()
        .optional()
        .describe('Why the answer is what it is. A learner sees this only after acting.'),
};
export const reviewSceneArms = {
    HOOK: sceneArms.HOOK.extend(drafted),
    CONCEPT: sceneArms.CONCEPT.extend(drafted),
    CASE: sceneArms.CASE.extend(drafted),
    /**
     * The one arm a reviewer sees differently, and the reason is the draft gate.
     *
     * A learner's `ARTICLE` must cite something — `012` §7 gives it a publish obligation and
     * `sceneIssues` refuses to publish without one. A *draft* may not have it yet: the
     * planner declines to invent evidence, so a row with no source produces exactly this.
     * Requiring it here made the review endpoint throw on the drafts a reviewer opens it to
     * fix, which is draft-permissive authoring inverted.
     */
    ARTICLE: sceneArms.ARTICLE.extend(drafted).partial({
        sourceId: true,
        sourceTitle: true,
    }),
    RECAP: sceneArms.RECAP.extend(drafted),
    REWARD: sceneArms.REWARD.extend(drafted),
    /**
     * The five mechanics, each missing the bound its served arm insists on — GEN-78.
     *
     * `ARTICLE`'s argument above, reaching the rest of the union: a reviewer may now change
     * a question's mechanic, and the moment after they do the scene is a Slider with no
     * track — real, saved, and the state this screen exists to resolve. `api-conventions`
     * §10 is the rule, `sceneIssueParts` writes every refusal, and `sceneArms` itself is
     * untouched, so a *learner* still cannot be served an incomplete scene.
     */
    CHOICE: sceneArms.CHOICE.extend({
        ...drafted,
        ...answered,
        options: z.array(reviewSceneItemSchema).max(SCENE_ITEMS_MAX.CHOICE),
    }).partial({ maxSelections: true }),
    RANK: sceneArms.RANK.extend({
        ...drafted,
        ...answered,
        items: z.array(reviewSceneItemSchema).max(SCENE_ITEMS_MAX.RANK),
    }),
    BUCKET: sceneArms.BUCKET.extend({
        ...drafted,
        ...answered,
        items: z.array(reviewSceneItemSchema).max(SCENE_ITEMS_MAX.BUCKET),
        buckets: z.array(sceneBucketSchema).max(SCENE_BUCKETS_MAX),
    }),
    SLIDER: sceneArms.SLIDER.extend({
        ...drafted,
        ...answered,
        targetMin: z.number().describe('Lowest value counted correct.'),
        targetMax: z.number().describe('Highest value counted correct.'),
    }).partial({
        min: true,
        max: true,
        step: true,
        unit: true,
        targetMin: true,
        targetMax: true,
    }),
    ALLOCATE: sceneArms.ALLOCATE.extend({
        ...drafted,
        ...answered,
        categories: z.array(reviewSceneItemSchema).max(SCENE_ITEMS_MAX.ALLOCATE),
    }).partial({ poolTotal: true }),
    SPEAK: sceneArms.SPEAK.extend({
        ...drafted,
        ...answered,
        counterpartBrief: z
            .string()
            .optional()
            .describe('Who the learner is speaking to, and what they want.'),
    }),
};
export const reviewSceneSchema = z
    .discriminatedUnion('kind', [
    reviewSceneArms.HOOK,
    reviewSceneArms.CONCEPT,
    reviewSceneArms.CASE,
    reviewSceneArms.ARTICLE,
    reviewSceneArms.RECAP,
    reviewSceneArms.REWARD,
    reviewSceneArms.CHOICE,
    reviewSceneArms.RANK,
    reviewSceneArms.BUCKET,
    reviewSceneArms.SLIDER,
    reviewSceneArms.ALLOCATE,
    reviewSceneArms.SPEAK,
])
    .describe('One scene, with everything a learner is not allowed to see.');
export const lessonReviewSchema = lessonSummarySchema
    .extend({
    /**
     * The colour the preview wears — the same `familyOf` the player reads, off the same
     * memberships.
     *
     * Derived and not stored, so it has to be served or the preview picks its own. A
     * draft tinted differently from the lesson it becomes is a preview answering *what
     * will this look like* with a colour that is not the answer.
     */
    ...tintFields,
    scenes: z
        .array(reviewSceneSchema)
        .describe('Every scene, in placement order. Never paginated.'),
    cast: lessonCastSchema.optional().describe('Absent when it names nobody.'),
    props: lessonPropsSchema,
    terms: z
        .array(lessonTermSchema)
        .describe('The terms this lesson uses, in order, each linked to the lesson that taught it whatever its status.'),
})
    .describe('A draft lesson as a reviewer reads it.');
/**
 * What the **console** reads: the lesson, plus what only an editor needs.
 *
 * Separate from `lessonReviewSchema` because the viewer validates that one, and a scene's
 * refcount is not something a renderer has any use for. Extending here keeps the payload
 * one request while leaving the rendered contract exactly what it was.
 */
export const lessonWorkspaceSchema = lessonReviewSchema
    .extend({
    /**
     * Whether this lesson is editable at all — GEN-64.
     *
     * Here rather than on `lessonSummarySchema`, which the player also reads: a learner
     * never sees a draft, so status there is always `PUBLISHED`.
     *
     * The console needs it because publishing a module publishes its lessons while
     * un-publishing the module does not put them back. Every write was then refused with
     * `Move the lesson back to DRAFT` and nothing could.
     */
    status: contentStatusSchema,
    writing: z
        .boolean()
        .describe('Whether an import can still write lessons into a module holding this one. The module’s publish is locked until it cannot, and the lesson’s is locked with it.'),
    /**
     * The labels, graded — 016 §6.
     *
     * On the workspace and not on the list row: a table of five lessons showing six
     * personas each is the workspace served as a grid, and a reviewer editing a grade
     * has to read the current one first.
     */
    personas: z.array(personaLabelSchema),
    tags: z.array(tagSlugSchema),
    usage: z
        .array(z.strictObject({
        sceneId: z.string(),
        lessons: z
            .number()
            .int()
            .describe('How many lessons place this scene, including this one.'),
    }))
        .describe('A list rather than a map: `z.record` has no shape, so the SDK would type it as any. §12.1 — more than one lesson forks on regeneration, and the console says so before the click.'),
    handEdited: z
        .array(z.string())
        .describe('The scenes somebody has corrected by hand — GEN-47. A whole-lesson regeneration leaves them alone, and the console says how many before the click rather than after the loss.'),
    /**
     * What the reviewer said about this lesson, and what the editor did — SKILL-03.
     *
     * On the workspace rather than a route of its own, for the reason `usage` is: the
     * reviewer reads a finding beside the scene it names, and a second request would put
     * the two on different render passes. `targetId` is the lesson or one of its scenes.
     */
    findings: z.array(contentReviewSchema),
    storySentences: storySentencesSchema.describe('The extra module-story sentences the lesson still holds, in scene order — GEN-131. Empty for a lesson built with no story.'),
    /**
     * The five quality scores and the verdict — GEN-89.
     *
     * Served rather than computed in the console, and computed from *every* unresolved
     * finding rather than from `findings` above, which is capped for display. A score
     * derived from the visible fifty would improve as findings scrolled off the panel.
     */
    qa: qaScorecardSchema,
})
    .describe('A draft lesson as the authoring console reads it.');
/**
 * Which of a draft's scenes can actually be played, and which are not finished — GEN-80.
 *
 * The arms above are draft-permissive, so a preview that *plays* one has to answer a
 * question the key-revealing pane never did. `sceneSchema` is that question, already
 * written and already what the API serves a learner through.
 *
 * The unfinished are named rather than dropped silently; the reasoning for both halves is
 * in `docs/decisions/preview-plays-the-lesson.md`.
 */
export function playableScenes(scenes) {
    const playable = [];
    const unfinished = [];
    for (const scene of scenes) {
        const { hint: _hint, body: _body, ...rest } = scene;
        const hint = servedHint(scene);
        const body = servedBody(scene);
        const parsed = sceneSchema.safeParse({
            ...rest,
            ...(hint !== undefined && { hint }),
            ...(body !== undefined && { body }),
        });
        if (parsed.success)
            playable.push(parsed.data);
        else
            unfinished.push({ position: scene.position, prompt: scene.prompt });
    }
    return { playable, unfinished };
}
//# sourceMappingURL=review.js.map